<template>
  <div>
    <div
      class="file-upload-container has-aspect-ratio"
      :class="{'border border-red': hasError}"
      :style="`--aspect-ratio:${aspectRatio}`"
      :qaid="qaid"
    >
      <div v-if="displayOnly">
        <div class="has-aspect-ratio" :style="`--aspect-ratio:${aspectRatio}`">
          <span class="file-uploads file-uploads-html5">
            <img v-if="isImage" :src="presetImageFileUrl" @error="reloadImage" @load="imageLoaded" />
            <i v-else class="material-icons md-48">insert_drive_file</i>
          </span>
        </div>

        <div>
          <trek-button
            text
            icon-only
            icon="cancel"
            class="remove-file-button"
            aria-label="Clear file"
            @click="clearFile"
          >
          </trek-button>
        </div>
      </div>

      <upload-component
        v-else
        ref="upload"
        v-model="files"
        :name="inputId"
        :input-id="inputId"
        :headers="headers"
        :accept="accept"
        :extensions="extensions"
        :post-action="isImage ? null : postActionUrl"
        @input-file="inputFile"
      >
        <pdl-loading :is-loading="loading">
          <template v-if="!fileUploaded">
            <i class="md-24">publish</i>
            <div v-html="clickToUploadMsg"></div>
          </template>
          <template v-else>
            <template v-if="isImage">
              <img :src="file" />
            </template>
            <span v-else>
              <i class="material-icons md-48">insert_drive_file</i>
            </span>
          </template>
        </pdl-loading>
      </upload-component>

      <div v-if="fileUploaded && !loading">
        <trek-button
          :id="buttonId"
          text
          icon-only
          icon="cancel"
          class="remove-file-button"
          aria-label="Clear file"
          @click="clearFile"
        >
        </trek-button>
      </div>
    </div>

    <div class="mt-25">
      <div v-if="computedFileName" class="text-sm text-gray-60">{{ computedFileName }}</div>
      <slot name="help">
        <div v-if="help" class="text-sm text-gray-30">{{ help }}</div>
      </slot>
    </div>
    <div v-if="uploadButton" class="buttons">
      <trek-button
        v-if="!$refs.upload || !$refs.upload.active"
        secondary
        small
        icon="cloud_upload"
        qaid="start-upload"
        @click.prevent="activateUpload"
      >
        <span>upload</span>
      </trek-button>
    </div>
  </div>
</template>

<script>
// https://lian-yue.github.io/vue-upload-component/
import UploadComponent from 'vue-upload-component';
import {PdlLoading} from '@pedal/pdl-loading';
import isEmpty from 'lodash/isEmpty';
import TrekButton from '@/components/TrekButton';
import {mapActions} from 'vuex';
const imageExtensions = 'gif,jpg,jpeg,png';
const imageAcceptHeaders = 'image/png, image/gif, image/jpeg';

export default {
  components: {UploadComponent, PdlLoading, TrekButton},
  props: {
    help: {
      type: String,
      default: 'Image file format only',
    },
    clickToUploadMsg: {
      type: String,
      default: 'Click to upload',
    },
    wrongFileTypeMsg: {
      type: String,
      default: 'Wrong file format selected',
    },
    fileTooLargeMsg: {
      type: String,
      default: 'File is too large',
    },
    readyToUploadMsg: {
      type: String,
      default: 'Ready to upload',
    },
    fileSize: {
      type: Number,
      // In Bytes. This is defaulted to 5000001 Bytes or 5MB
      default: 5000001,
    },
    aspectRatio: {
      type: String,
      default: '1',
    },
    buttonId: {
      type: String,
      default: undefined,
    },
    inputId: {
      type: String,
      default: undefined,
    },
    postAction: {
      type: String,
      default: undefined,
    },
    csrfToken: {
      type: String,
      default: undefined,
    },
    uploadButton: {
      type: Boolean,
      default: true,
    },
    qaid: {
      type: String,
      default: 'file-upload',
    },
    inFileName: {
      type: String,
      default: undefined,
    },
    inPresetImageFileUrl: {
      type: String,
      default: undefined,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    baseProperties: {
      type: Array,
      default() {
        return [];
      },
    },
    extensions: {
      type: String,
      default: imageExtensions,
    },
    accept: {
      type: String,
      default: imageAcceptHeaders,
    },
    uploadOnInput: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      file: {},
      files: [],
      fileName: null,
      loading: false,
      headers: {},
      presetImageFileUrl: this.inPresetImageFileUrl,
      imageReloadInterval: null,
    };
  },

  computed: {
    name() {
      return `upload-${Math.random()
        .toString(32)
        .substring(2, 8)}`;
    },

    fileUploaded() {
      return !isEmpty(this.files);
    },

    computedFileName() {
      let filename = this.inFileName || this.fileName;
      if (!filename && this.presetImageFileUrl) {
        filename = this.getFilenameFromUrl(this.presetImageFileUrl);
      }

      return filename;
    },

    computedFileExtension() {
      return this.computedFileName ? this.computedFileName.split('.').pop() : null;
    },

    isImage() {
      return imageExtensions.includes(this.computedFileExtension);
    },

    postActionUrl() {
      if (!this.postAction) {
        return `${window.location.pathname}?CSRFToken=${this.csrfToken}`;
      } else {
        return this.postAction;
      }
    },

    displayOnly() {
      return this.presetImageFileUrl && this.presetImageFileUrl.length > 0;
    },
  },

  watch: {
    inPresetImageFileUrl() {
      this.presetImageFileUrl = this.inPresetImageFileUrl;
    },
  },

  methods: {
    ...mapActions('productFeedback', ['updateFlag']),

    reloadImage(event) {
      if (this.imageReloadInterval == null) {
        const errEvent = event;
        var repetition = 0;
        let ref = this;
        this.imageReloadInterval = setInterval(() => {
          errEvent.target.src = this.inPresetImageFileUrl;
          repetition++;
          if (repetition > 4) {
            clearInterval(this.imageReloadInterval);
            ref.updateFlag({flagName: 'isLoading', value: false});
          }
        }, 4000);
      }
    },
    imageLoaded() {
      if (this.imageReloadInterval) {
        clearInterval(this.imageReloadInterval);

        this.updateFlag({flagName: 'isLoading', value: false});
      }
      this.updateFlag({flagName: 'isLoading', value: false});
    },
    getFilenameFromUrl(url) {
      if (!url) {
        return '';
      }

      const matches = url
        .split('?')[0]
        .toString()
        .match(/\/([^/]+)$/);
      return matches && matches.length > 1 ? matches[1] : '';
    },

    activateUpload() {
      this.$refs.upload.active = true;
      this.$emit('activate-upload');
    },

    handleFileUpload(file) {
      setTimeout(() => {
        const reader = new FileReader();

        reader.readAsDataURL(file.file);

        reader.addEventListener('load', () => {
          this.file = reader.result;
          this.fileName = file.name;
          this.loading = false;
          this.$notify({
            dangerouslyUseHTMLString: true,
            type: 'success',
            message: `${file.name} ${this.readyToUploadMsg}`,
          });

          this.$emit('file-uploaded', {name: file.name, encodedFile: this.file});
        });
      }, 1000);
    },

    clearFile() {
      this.presetImageFileUrl = null;
      this.files = [];
      this.file = null;
      this.fileName = null;
      this.$emit('clear-file');
    },

    initUpload(file) {
      this.$emit('initiate-file-upload', file);
      return;
    },

    fileExtensionCheck(newFile) {
      let newfileExt = newFile.name.slice(newFile.name.lastIndexOf('.') + 1);
      return this.extensions.includes(newfileExt.toLowerCase());
    },

    inputFile(newFile, oldFile) {
      if (newFile && !oldFile) {
        let valid = true;

        // Validate file type/extension
        if (!this.fileExtensionCheck(newFile)) {
          valid = false;
          this.clearFile();
          this.$notify({
            type: 'alert',
            message: this.bp('fileTypeError') || this.wrongFileTypeMsg,
          });
        }

        // Validate file size
        if (valid && newFile.size > this.fileSize) {
          valid = false;
          this.clearFile();
          this.$notify({
            type: 'alert',
            message: this.bp('fileTooLarge') || this.fileTooLargeMsg,
          });
        }

        // handle adding file
        if (valid) {
          this.fileName = newFile.name;

          if (this.uploadOnInput) {
            this.handleFileUpload(newFile);
          }
        }
      }

      if (newFile && oldFile) {
        // handle updating file
      }

      if (newFile && oldFile && !newFile.active && oldFile.active) {
        // Get response data
        this.files = [];
        this.$emit('response', newFile.response);
        const toastMessage = newFile.response.bulkUploadResponse.meta.feedback.message.basePropertyValue;

        if (newFile.response.tooManyUploads === true) {
          this.$notify({
            type: 'alert',
            message: toastMessage,
          });
        } else if (newFile.response.successCount > 0) {
          this.$notify({
            type: 'success',
            message: toastMessage,
          });
        }
      }
      if (!newFile && oldFile) {
        // handle removing the file here
      }
    },
  },
};
</script>
